import React from 'react'
import { useTranslation } from 'next-i18next'
import styled from '@emotion/styled'

import Column from 'modules/ui/atoms/Layout/Column'
import Row from 'modules/ui/atoms/Layout/Row'
import InputField from 'modules/ui/molecules/FormComponents/InputField'
import SelectField from 'modules/ui/molecules/FormComponents/SelectField'
import Placeloader from 'modules/ui/atoms/Loaders/Placeloader'
import { UiIcon } from 'modules/ui/atoms/Icon'
import { DEFAULT_THEME_COLORS } from 'css/theme'
import Label from 'modules/ui/atoms/Typography/Label'

import { Question } from 'models/Question'
import { useAddressForm } from '../address/useAddressForm'

const AddLine2Link = styled.div`
  color: ${DEFAULT_THEME_COLORS['--theme-color--brand-primary']};
  cursor: pointer;
  font-size: 14px;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid ${DEFAULT_THEME_COLORS['--theme-color--brand-primary']};
    outline-offset: 2px;
  }
`

interface AddressQuestionFieldProps {
  addressQuestions: Omit<Question, 'id'>[]
  showRequired: boolean
}

// Field IDs for address fields - important for autofill & autocomplete
const ADDRESS_FIELD_IDS = {
  country: 'country',
  addressLine1: 'addressLine1',
  addressLine2: 'addressLine2',
  city: 'city',
  state: 'state',
  zipCode: 'zipCode',
}

// Mapping from backend question tags to internal field names
const BACKEND_TAG_TO_FIELD_MAPPING = {
  address: 'addressLine1',
  address2: 'addressLine2',
  postal_code: 'zipCode',
  country: 'country',
  city: 'city',
  state: 'state',
} as const

const AddressQuestionField = ({ addressQuestions, showRequired }: AddressQuestionFieldProps) => {
  const { t } = useTranslation()

  // Filter address questions based on showRequired
  const filteredQuestions = addressQuestions.filter((q) => q.is_required === showRequired)

  // Get individual question configs
  const getQuestionByTag = (tag: string) => filteredQuestions.find((q) => q.tag === tag)

  const addressQuestion = getQuestionByTag('address')
  const address2Question = getQuestionByTag('address2')
  const cityQuestion = getQuestionByTag('city')
  const stateQuestion = getQuestionByTag('state')
  const postalCodeQuestion = getQuestionByTag('postal_code')
  const countryQuestion = getQuestionByTag('country')

  // Helper function to get the internal field name from backend tag
  const getFieldNameFromTag = (tag: string): string => {
    return BACKEND_TAG_TO_FIELD_MAPPING[tag as keyof typeof BACKEND_TAG_TO_FIELD_MAPPING] || tag
  }

  // Use the shared hook with address question field IDs
  const {
    showLine2Input: showAddress2Input,
    autoComplete,
    handleAddLine2Click: handleAddAddress2Click,
    handleAddLine2KeyPress: handleAddAddress2KeyPress,
    handleKeyDown,
    handleOnFocus,
    handleOnBlur,
    googlePlacesRef: ref,
    countryOptions,
    stateOptions,
    statesIsFetching: isStatesLoading,
    watchCountry,
    fieldError,
  } = useAddressForm({
    fieldIds: ADDRESS_FIELD_IDS,
    enableLine2: true,
  })

  // If no address questions match the required filter, don't render anything
  if (filteredQuestions.length === 0) return null

  return (
    <Column>
      {/* Address Line 1 */}
      {addressQuestion && addressQuestion.tag && (
        <Row>
          <Column>
            <InputField
              required={addressQuestion.is_required}
              id={getFieldNameFromTag(addressQuestion.tag)}
              label={addressQuestion.label}
              error={fieldError(getFieldNameFromTag(addressQuestion.tag))}
              onKeyDown={handleKeyDown}
              inputRef={ref}
              overwriteValueWithFocus={true}
              autoComplete={autoComplete}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
              placeholder={t('AddressFields.address.placeholder', 'Enter a location')}
              dataId="address-question-input"
            />
          </Column>
        </Row>
      )}

      {/* Address Line 2 - Always optional, shown as link or input */}
      {address2Question && (
        <Row>
          <Column>
            {!showAddress2Input ? (
              <AddLine2Link
                onClick={handleAddAddress2Click}
                onKeyDown={handleAddAddress2KeyPress}
                tabIndex={0}
                role="button"
                aria-label={t('AddressFields.address2.addLabel', 'Add apartment, suite, floor')}
              >
                <UiIcon
                  w={22}
                  h={22}
                  name="add_circle"
                  color={DEFAULT_THEME_COLORS['--theme-color--brand-primary']}
                />

                <Label size="sm" color={DEFAULT_THEME_COLORS['--theme-color--brand-primary']}>
                  {t('OrderAddress.label.addLine2Prompt', 'Add apt., suite, floor')}
                </Label>
              </AddLine2Link>
            ) : (
              <InputField
                required={false} // Address 2 is never required per requirements
                id={getFieldNameFromTag(address2Question.tag || 'address2')}
                label={address2Question.label}
                error={fieldError(getFieldNameFromTag(address2Question.tag || 'address2'))}
                autoComplete="address-line2"
                dataId="address2-question-input"
              />
            )}
          </Column>
        </Row>
      )}

      {/* Country */}
      {countryQuestion && countryQuestion.tag && (
        <Row>
          <Column>
            <SelectField
              required={countryQuestion.is_required}
              id={getFieldNameFromTag(countryQuestion.tag)}
              options={countryOptions}
              label={countryQuestion.label}
              error={fieldError(getFieldNameFromTag(countryQuestion.tag))}
              placeholder={t('AddressFields.country.placeholder', 'Select country')}
              dataId="country-question-select"
              autoComplete="country"
            />
          </Column>
        </Row>
      )}

      {/* State and Postal Code */}
      <Row>
        {stateQuestion && stateQuestion.tag && (
          <Column>
            {isStatesLoading ? (
              <Placeloader
                style={{ minHeight: '46px', borderRadius: '8px', margin: '4px 0', width: '100%' }}
              />
            ) : (
              <SelectField
                required={stateQuestion.is_required}
                id={getFieldNameFromTag(stateQuestion.tag)}
                options={stateOptions}
                label={stateQuestion.label}
                error={fieldError(getFieldNameFromTag(stateQuestion.tag))}
                placeholder={t('AddressFields.state.placeholder', 'Select state')}
                dataId="state-question-select"
                autoComplete="address-level1"
              />
            )}
          </Column>
        )}

        {postalCodeQuestion && postalCodeQuestion.tag && (
          <Column>
            <InputField
              required={postalCodeQuestion.is_required}
              id={getFieldNameFromTag(postalCodeQuestion.tag)}
              label={
                watchCountry === 'US'
                  ? t('OrderAddress.label.zipCode', 'Zip Code')
                  : t('OrderAddress.label.postalCode', 'Postal Code')
              }
              error={fieldError(getFieldNameFromTag(postalCodeQuestion.tag))}
              autoComplete="postal-code"
              dataId="postal-code-question-input"
            />
          </Column>
        )}
      </Row>

      {/* City */}
      {cityQuestion && cityQuestion.tag && (
        <Row>
          <Column>
            <InputField
              required={cityQuestion.is_required}
              id={getFieldNameFromTag(cityQuestion.tag)}
              label={cityQuestion.label}
              error={fieldError(getFieldNameFromTag(cityQuestion.tag))}
              autoComplete="address-level2"
              dataId="city-question-input"
            />
          </Column>
        </Row>
      )}
    </Column>
  )
}

export default AddressQuestionField
